import React, { Component } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { SuiContainer } from '@splashthat/sui';
import { KEY_ENTER, KEY_RETURN } from 'keycode-js';
import { INPUT_TYPE_SELECT, INPUT_TYPE_MULTI_SELECT, INPUT_TYPE_RADIO } from 'common/entities/formNode/inputTypes';

import CheckboxOptionsDropdown from './CheckboxOptionsDropdown';
import DefaultOptionsInput from './DefaultOptionsInput';

const hasNodeTypeSuggestions = (nodeType) =>
    nodeType === INPUT_TYPE_SELECT || nodeType === INPUT_TYPE_MULTI_SELECT || nodeType === INPUT_TYPE_RADIO;

const SUGGESTIONS_LABEL = 'Pick your options';

export default class NodeOptionsManager extends Component {
    state = {
        inputValue: '',
    };

    static propTypes = {
        operandNode: PropTypes.object,
        operandValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
        onChange: PropTypes.func.isRequired,
        onValueDefined: PropTypes.func.isRequired,
        useAdvancedTagsInput: PropTypes.bool.isRequired,
        displayCheckboxOptions: PropTypes.bool,
    };

    static defaultProps = {
        operandNode: null,
        operandValue: '',
        displayCheckboxOptions: false,
    };

    handleChange = (event, valueObject) => {
        // SuiAutocompleteInput returns 2nd argument as an object.
        // Grabbing the actual value to match other SUI input change signatures.
        this.props.onChange(event, valueObject.value);
    };

    onChangeInputValue = (event, value) => {
        this.setState((prevState) => ({
            ...prevState,
            inputValue: value,
        }));
    };

    // Use this when we have suggestions array.
    handleSuggestionSelect = (event, suggestion) => {
        this.props.onValueDefined(suggestion);
    };

    handleKeyPress = (event) => {
        if (event.which === KEY_ENTER || event.which === KEY_RETURN) {
            this.props.onValueDefined(this.state.inputValue);
            this.onChangeInputValue(null, '');
        }
    };

    handleButtonClick = (event) => {
        this.props.onValueDefined(this.state.inputValue);
        this.onChangeInputValue(null, '');
    };

    getAutocompleteOptions = (currentValue) =>
        this.props.useAdvancedTagsInput
            ? {
                  value: { label: SUGGESTIONS_LABEL, value: null },
                  onSuggestionSelect: this.handleSuggestionSelect,
                  preventSuggestions: true,
              }
            : {
                  value: currentValue,
                  onChange: this.handleChange,
              };

    render() {
        const { operandNode, operandValue, displayCheckboxOptions, useAdvancedTagsInput } = this.props;

        const optionIds = get(operandNode, 'config.options.result', []);
        const options = get(operandNode, 'config.options.entities', {});

        const suggestions = optionIds.map((id) => ({
            value: options[id].value,
            label: options[id].label,
        }));

        const hasSuggestions = !!suggestions.length && hasNodeTypeSuggestions(operandNode.inputType);

        const selectedOptionId = optionIds.find((id) => options[id].value === operandValue);

        const currentValue = {
            value: operandValue,
            label: selectedOptionId ? options[selectedOptionId].label : operandValue,
        };

        const userInputProps =
            !hasSuggestions && useAdvancedTagsInput
                ? {
                      value: this.state.inputValue,
                      onChange: this.onChangeInputValue,
                      onKeyPress: this.handleKeyPress,
                      onClick: this.handleButtonClick,
                  }
                : {
                      suggestionValues: suggestions,
                      ...this.getAutocompleteOptions(currentValue),
                  };

        return (
            <SuiContainer variation="full-width">
                {displayCheckboxOptions ? (
                    <CheckboxOptionsDropdown value={currentValue.value} onChange={this.handleChange} />
                ) : (
                    <DefaultOptionsInput
                        hasSuggestions={hasSuggestions}
                        useAdvancedTagsInput={useAdvancedTagsInput}
                        {...userInputProps}
                    />
                )}
            </SuiContainer>
        );
    }
}
